/** Has the unattended work run — the panel on `/administration` (OPS-01).
 *
 * `GET /api/v1/ops/liveness` answered for weeks and nothing rendered it. Four properties, each of
 * which is a way a liveness screen goes wrong while looking right:
 *
 * 1. **A zero is not health on its own.** `extraction_detected` is `false` because no path in the
 *    product can leave a document mid-extraction. The count of stuck extractions is then not zero,
 *    it is *absent* — so the screen prints **no count at all** and says which kind of nothing it
 *    is. A "0 stuck" with a tick beside it is F10: a true value dressed as a trust claim.
 * 2. **`alerting_delivered` says a message arrives, not that anybody read it.** It is shown as a
 *    fact about the path, never as the screen's verdict, and when it is `false` the screen says
 *    nobody has been told.
 * 3. **A failed read is a failure.** `Loaded` renders it as one, with a way to try again. This
 *    panel has no default that turns an error into an answer.
 * 4. **A role that cannot read this is told so, not shown a request that 403s** (design rule 6).
 *    The route is Admin and Approver; `useLiveness` is not even enabled for anyone else.
 *
 * **Nothing here is a copy of a rule.** The sentences for a state describe what the state *is*; the
 * numbers, the window and the alert fields are rendered exactly as served. An alert is a **code
 * with a declared field set** (`ops/alerts.py`), so the code is printed as the code and its fields
 * as the fields — no prose is written around either, and nothing is shown that the payload does
 * not contain. A client that composed a sentence from an alert would be authoring the one thing
 * the backend deliberately refuses to let carry content.
 *
 * **No green, no tick.** Colour carries emphasis, never meaning (design rule 8), and nothing on
 * this panel is rendered in `--ok`: that token is for something that passed a check, and the
 * most this endpoint can say is that no check is currently raising an alert.
 */

import { Loaded } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { humanise, opsCodeWords, opsFieldWords, sentenceCase } from "../lib/customerWords";
import { useMe } from "../api/auth";
import { useLiveness } from "../api/ops";
import type { Liveness, LivenessAlert, LivenessAnchor, LivenessExtraction } from "../api/ops";
import own from "./LivenessPanel.module.css";
import styles from "./Screen.module.css";

/** The roles the route admits. **A statement about the server, kept here only so the client does
 * not ask a question it can predict the refusal of** — if it drifts, the failure is a visible 403
 * on the read, which `Loaded` reports, not a silent wrong answer. */
const MAY_READ = new Set(["admin", "approver"]);

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type Tone = "attention" | "neutral";

/** The anchor's six states, each in its own words. None is folded into another: `nothing_to_anchor`
 * and `up_to_date` are both quiet and are different facts, and `never_anchored` is quiet in the
 * data and the most serious thing here. */
const ANCHOR_STATES: Record<string, { label: string; tone: Tone; says: string }> = {
  nothing_to_anchor: {
    label: "nothing to anchor",
    tone: "neutral",
    says: "There are no ledger entries yet, so there is nothing for an anchor to attest. That is not applicable, and it is not a pass.",
  },
  never_anchored: {
    label: "never anchored",
    tone: "attention",
    says: "Ledger entries exist and no anchor has ever been written for this workspace.",
  },
  up_to_date: {
    label: "up to date",
    tone: "neutral",
    says: "The latest ledger entry is anchored.",
  },
  pending: {
    label: "pending",
    tone: "neutral",
    says: "Entries are waiting for the next anchor, and the oldest is still inside the allowed wait. The run is due, not late.",
  },
  overdue: {
    label: "overdue",
    tone: "attention",
    says: "An entry has waited longer than the allowed wait for an anchor.",
  },
  ahead_of_chain: {
    label: "ahead of the ledger",
    tone: "attention",
    says: "An anchor exists for a sequence the ledger does not reach. That is a different fact from a late run, and it is not treated as one.",
  },
};

const EXTRACTION_STATES: Record<string, string> = {
  in_flight: "in flight",
  stalled: "stalled",
  undated: "age unknown",
};

function fact(value: number | null): string {
  return value === null ? "none recorded" : String(value);
}

function Fact({ name, label, value }: { name: string; label: string; value: string }) {
  return (
    <div className={cls(own.fact)} data-fact={name}>
      <dt className={cls(own.factLabel)}>{label}</dt>
      <dd className={cls(own.factValue)}>{value}</dd>
    </div>
  );
}

function AnchorSection({ anchor }: { anchor: LivenessAnchor }) {
  const known = ANCHOR_STATES[anchor.state];
  const tone: Tone = known?.tone ?? "neutral";
  return (
    <section
      className={cls(own.section)}
      data-section="anchor"
      data-state={anchor.state}
      data-recognised={known ? "true" : "false"}
      data-needs-attention={String(anchor.needs_attention)}
    >
      <div className={cls(own.head)}>
        <h3 className={cls(own.title)}>Ledger anchor</h3>
        <span className={cls(own.chip, (tone === "attention" || anchor.needs_attention) && own.attention)}>
          {sentenceCase(known?.label ?? humanise(anchor.state))}
        </span>
        {anchor.needs_attention ? (
          <span className={cls(own.chip, own.attention)}>needs attention</span>
        ) : null}
      </div>
      <p className={cls(own.says)}>
        {/* **A state this screen does not know is said, not guessed at.** The server owns the
            set of six; if a seventh arrives it is printed as served and called what it is. */}
        {known?.says ??
          "This screen does not recognise this state, so it is not being reported as healthy."}
      </p>
      <dl className={cls(own.facts)}>
        <Fact name="entries" label="Ledger entries" value={String(anchor.entries)} />
        <Fact name="head_seq" label="Latest entry" value={fact(anchor.head_seq)} />
        <Fact
          name="last_anchored_seq"
          label="Latest anchored entry"
          value={fact(anchor.last_anchored_seq)}
        />
        <Fact
          name="unanchored_entries"
          label="Entries waiting"
          value={String(anchor.unanchored_entries)}
        />
        <Fact
          name="waiting_hours"
          label="Oldest waiting for (hours)"
          value={fact(anchor.waiting_hours)}
        />
        <Fact name="window_hours" label="Allowed wait (hours)" value={String(anchor.window_hours)} />
      </dl>
    </section>
  );
}

function ExtractionRow({ finding }: { finding: LivenessExtraction }) {
  return (
    <li
      className={cls(own.row)}
      data-extraction={finding.document_id}
      data-state={finding.state}
      data-needs-attention={String(finding.needs_attention)}
    >
      <code>{finding.document_id}</code>
      <span className={cls(own.chip, finding.needs_attention && own.attention)}>
        {sentenceCase(EXTRACTION_STATES[finding.state] ?? humanise(finding.state))}
      </span>
      <span className={cls(own.rowDetail)}>
        waiting {fact(finding.waiting_hours)} of an allowed {finding.window_hours} hours
      </span>
    </li>
  );
}

function ExtractionSection({ liveness }: { liveness: Liveness }) {
  const observable = liveness.extraction_detected;
  const found = liveness.extractions;
  return (
    <section
      className={cls(own.section)}
      data-section="extraction"
      data-observable={String(observable)}
    >
      <div className={cls(own.head)}>
        <h3 className={cls(own.title)}>Extraction</h3>
        <span className={cls(own.chip)}>{observable ? "observed" : "not observable"}</span>
      </div>

      {observable ? null : (
        <p className={cls(own.says)} data-testid="extraction-not-observable">
          {/* **No number is printed in this branch.** The absence of a count is the message: there
              is no zero here to misread as a clean result, because there is no state to count. */}
          No path in this product can leave a document part-way through extraction, so there is
          nothing to count. That is the absence of the state, not evidence that nothing is stuck,
          and it says nothing about whether extraction is working.
        </p>
      )}

      {/* **Rendered whenever the server sent any, whatever the flag says.** If the two ever
          disagree, hiding the rows to agree with the flag would be this screen choosing which of
          the server's facts to believe. */}
      {found.length > 0 ? (
        <ul className={cls(own.rows)}>
          {found.map((finding) => (
            <ExtractionRow key={finding.document_id} finding={finding} />
          ))}
        </ul>
      ) : observable ? (
        <p className={cls(own.says)} data-testid="extraction-none-in-flight">
          No document is part-way through extraction.
        </p>
      ) : null}
    </section>
  );
}

function AlertItem({ alert }: { alert: LivenessAlert }) {
  const entries = Object.entries(alert.fields).map(([key, value]) => ({ key, label: opsFieldWords(key), value }));
  return (
    <li className={cls(own.alert)} data-alert={alert.code} title={alert.code}>
      {/* The alert in a sentence (U8). The code stays on the row (`data-alert`, the hover) for whoever
          reads it back to us; it is not the visible text. */}
      <span className={cls(own.code)}>{opsCodeWords(alert.code)}</span>
      {/* **Exactly the fields served**, named for a person. The backend declares the set each code
          carries and refuses any other; a label here is wording for a field it already sent. */}
      <dl className={cls(own.facts)}>
        {entries.map(({ key, label, value }) => (
          <Fact key={key} name={key} label={label} value={value === null ? "not set" : String(value)} />
        ))}
      </dl>
    </li>
  );
}

function AlertsSection({ liveness }: { liveness: Liveness }) {
  const raised = liveness.alerts;
  return (
    <section
      className={cls(own.section)}
      data-section="alerts"
      data-count={String(raised.length)}
      data-needs-attention={String(liveness.needs_attention)}
    >
      <div className={cls(own.head)}>
        <h3 className={cls(own.title)}>Alerts raised</h3>
        {liveness.needs_attention ? (
          <span className={cls(own.chip, own.attention)}>needs attention</span>
        ) : null}
      </div>
      {raised.length > 0 ? (
        <ul className={cls(own.alerts)}>
          {raised.map((alert) => (
            <AlertItem key={`${alert.code}:${JSON.stringify(alert.fields)}`} alert={alert} />
          ))}
        </ul>
      ) : (
        <p className={cls(own.says)} data-testid="no-alerts">
          Nothing is raising an alert from what can be observed.
          {liveness.extraction_detected
            ? ""
            : " Extraction cannot be observed, so this is silent about it."}
        </p>
      )}
    </section>
  );
}

function DeliverySection({ delivered }: { delivered: boolean }) {
  return (
    <section
      className={cls(own.section)}
      data-section="delivery"
      data-delivered={String(delivered)}
    >
      <div className={cls(own.head)}>
        <h3 className={cls(own.title)}>Alert delivery</h3>
        <span className={cls(own.chip, !delivered && own.attention)}>
          {delivered ? "reaches an address" : "not delivered"}
        </span>
      </div>
      <p className={cls(own.says)}>
        {delivered
          ? "A message reaches a confirmed address. That is a statement about the path, not about people: it does not mean anyone read a message or acted on one."
          : "Nothing is sent anywhere, so nobody has been told. An alert on this screen is seen only by someone opening it."}
      </p>
    </section>
  );
}

function LivenessBody({ liveness }: { liveness: Liveness }) {
  return (
    <div className={cls(own.body)} data-testid="liveness" data-needs-attention={String(liveness.needs_attention)}>
      <AnchorSection anchor={liveness.anchor} />
      <ExtractionSection liveness={liveness} />
      <AlertsSection liveness={liveness} />
      <DeliverySection delivered={liveness.alerting_delivered} />
    </div>
  );
}

export function LivenessPanel() {
  const { data: me } = useMe();
  const allowed = me ? MAY_READ.has(me.role) : false;
  const liveness = useLiveness(allowed);

  if (!allowed) {
    return (
      <Panel title="Unattended work" column="1 / 9">
        <p className={styles.note} data-testid="liveness-restricted">
          {/* Told who can, rather than shown a request that fails (design rule 6). */}
          An <strong>Admin</strong> or an <strong>Approver</strong> sees whether anchoring and
          extraction have run.
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Unattended work"
      sub="whether the work that runs without anyone present has run"
      column="1 / 9"
    >
      <Loaded query={liveness} of="The liveness check">
        {(data) => <LivenessBody liveness={data} />}
      </Loaded>
    </Panel>
  );
}
